import { get, isEmpty } from 'lodash';

// One of these describes everything the drawer needs to render, so the component itself never has to
// reason about the combination of loading flags and an empty payload. Mirrors the approach in
// org-view-state.js: keep the decisions in a pure function so they are unit-testable under CRA jest.
export const CHAIN_STATES = {
  LOADING: 'LOADING',
  // The person is not in the Entra mirror at all — an external cardholder, or a directory row that
  // has not synced yet. Distinct from ALONE so the copy can say why there is nothing to show.
  NOT_IN_DIRECTORY: 'NOT_IN_DIRECTORY',
  // In the directory, but with no manager above and nobody below.
  ALONE: 'ALONE',
  READY: 'READY',
};

export const deriveChainState = ({ isLoading, data } = {}) => {
  if (isLoading) {
    return CHAIN_STATES.LOADING;
  }

  // The backend answers 200 with a null subject rather than 404 for someone outside the mirror.
  if (isEmpty(data) || isEmpty(get(data, 'subject'))) {
    return CHAIN_STATES.NOT_IN_DIRECTORY;
  }

  if (isEmpty(get(data, 'chain')) && isEmpty(get(data, 'directReports'))) {
    return CHAIN_STATES.ALONE;
  }

  return CHAIN_STATES.READY;
};

// Top-down for display: the chain arrives nearest-manager-first, but people read a hierarchy from the
// top, so reverse it and put the subject last. The subject is flagged so the row can be highlighted.
export const toDisplayRows = (data) => {
  const subject = get(data, 'subject');
  if (isEmpty(subject)) {
    return [];
  }

  const chain = get(data, 'chain', []);

  return [
    ...[...chain].reverse().map((person) => ({ ...person, isSubject: false })),
    { ...subject, isSubject: true },
  ];
};

export const reportCount = (data) => get(data, 'directReports', []).length;

// Only worth offering the panel for someone who could plausibly be in the directory. Cardholders are
// frequently external contractors with no Entra account, so an absent email means "do not offer".
export const canShowChainFor = (email) => !isEmpty(email) && email.includes('@');
